import { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Alert, AlertTitle, alpha,
} from '@mui/material';
import {
  buildPackageGroupCode,
  getDuplicateRentalPackageMessage,
  type RentalPackageGroup,
  type RentalVehiclePrice,
  normalizeRentalPackageHours,
  normalizeRentalPackageKm,
  validateRentalExtraKmPrice,
  validateRentalGraceMinutes,
  validateRentalPackageBundle,
  validateRentalPackageHours,
  validateRentalPackageKm,
  validateRentalVehiclePrice,
} from '../../utils/rentalPackages';
import { vehiclesForRental } from '../../utils/masterRows';
import NonNegativeNumberField from '../ui/forms/NonNegativeNumberField';
import {
  RENTAL_PACKAGE_GRACE_DEFAULT_BIKE,
  RENTAL_PACKAGE_GRACE_DEFAULT_CAR,
  RENTAL_PACKAGE_GRACE_MAX,
  RENTAL_PACKAGE_GRACE_MAX_DIGITS,
  RENTAL_PACKAGE_GRACE_MIN,
  RENTAL_PACKAGE_EXTRA_KM_MAX,
  RENTAL_PACKAGE_EXTRA_KM_MAX_DIGITS,
  RENTAL_PACKAGE_HOURS_MAX,
  RENTAL_PACKAGE_HOURS_MIN,
  RENTAL_PACKAGE_KM_MAX,
  RENTAL_PACKAGE_KM_MIN,
  RENTAL_PACKAGE_PRICE_MAX,
  RENTAL_PACKAGE_PRICE_MAX_DIGITS,
  RENTAL_PACKAGE_PRICE_MIN,
  TRANSPORT_RENTAL_SERVICE_SLUG,
} from '../../constants/transportPartnerFlow';
import { chargeFieldLabel, getCurrencySymbol } from '../../utils/currency';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';

const helperTextProps = { sx: { color: 'text.primary', opacity: 0.82, fontWeight: 500 } } as const;
const disabledFieldSx = {
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'inherit',
    color: 'text.primary',
    opacity: 0.92,
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: 'text.primary',
    opacity: 0.72,
  },
} as const;

interface ZoneOption { _id: string; name: string; code?: string; currency?: string }

interface VehicleTypeOption {
  code: string;
  name: string;
  serviceTypes?: string;
}

interface VehiclePriceDraft {
  price: number | null;
  graceTimeMin: number;
  extraKmPrice: number;
}

export interface RentalPackageFormState {
  packageHr: number;
  packageKm: number;
  zoneCode: string;
  packageGroupCode: string;
  vehiclePrices: RentalVehiclePrice[];
}

interface RentalPackageDrawerProps {
  open: boolean;
  onClose: () => void;
  editingGroup: RentalPackageGroup | null;
  zones: ZoneOption[];
  vehicleTypes: VehicleTypeOption[];
  serviceSlug: string;
  /** Unified transport rental — no bike/car split in defaults or duplicate checks */
  unifiedTransportRental?: boolean;
  existingGroups?: RentalPackageGroup[];
  /** When set, package is always created for this zone only */
  lockedZoneCode?: string;
  /** Display name for the locked zone */
  lockedZoneName?: string;
  /** Pre-select zone when adding (e.g. zone filter chip on list page) */
  defaultZoneCode?: string;
  loading?: boolean;
  onSave: (form: RentalPackageFormState, editingGroup: RentalPackageGroup | null) => void;
}

function defaultGraceMinutes(serviceSlug: string, unifiedTransportRental?: boolean) {
  if (unifiedTransportRental || serviceSlug === TRANSPORT_RENTAL_SERVICE_SLUG) {
    return RENTAL_PACKAGE_GRACE_DEFAULT_CAR;
  }
  return serviceSlug === 'bike-rental' ? RENTAL_PACKAGE_GRACE_DEFAULT_BIKE : RENTAL_PACKAGE_GRACE_DEFAULT_CAR;
}

function defaultPackageHours(serviceSlug: string, unifiedTransportRental?: boolean) {
  if (unifiedTransportRental || serviceSlug === TRANSPORT_RENTAL_SERVICE_SLUG) return 4;
  return serviceSlug === 'bike-rental' ? 2 : 4;
}

function defaultPackageKm(serviceSlug: string, unifiedTransportRental?: boolean) {
  if (unifiedTransportRental || serviceSlug === TRANSPORT_RENTAL_SERVICE_SLUG) return 40;
  return serviceSlug === 'bike-rental' ? 30 : 40;
}

function buildEmptyVehiclePrices(
  rentalVehicles: VehicleTypeOption[],
  serviceSlug: string,
  unifiedTransportRental?: boolean,
): Record<string, VehiclePriceDraft> {
  const grace = defaultGraceMinutes(serviceSlug, unifiedTransportRental);
  const next: Record<string, VehiclePriceDraft> = {};
  for (const v of rentalVehicles) {
    next[v.code] = { price: null, graceTimeMin: grace, extraKmPrice: 0 };
  }
  return next;
}

export default function RentalPackageDrawer({
  open,
  onClose,
  editingGroup,
  zones,
  vehicleTypes,
  serviceSlug,
  unifiedTransportRental = false,
  existingGroups = [],
  lockedZoneCode,
  lockedZoneName,
  defaultZoneCode,
  loading,
  onSave,
}: RentalPackageDrawerProps) {
  useTenantCurrency();
  const [packageHr, setPackageHr] = useState(4);
  const [packageKm, setPackageKm] = useState(40);
  const [zoneCode, setZoneCode] = useState('');
  const [prices, setPrices] = useState<Record<string, VehiclePriceDraft>>({});

  const rentalVehicles = useMemo(
    () => vehiclesForRental(vehicleTypes),
    [vehicleTypes],
  );
  const selectedZone = zones.find((z) => z.code === (zoneCode || lockedZoneCode));
  const zoneCurrency = selectedZone?.currency;
  const moneyMark = getCurrencySymbol(zoneCurrency);

  const resolvedLockedZoneName = useMemo(() => {
    if (!lockedZoneCode) return '';
    if (lockedZoneName) return lockedZoneName;
    const match = zones.find((z) => z.code === lockedZoneCode);
    return match?.name || '';
  }, [lockedZoneCode, lockedZoneName, zones]);

  const initialZoneCode = useMemo(() => {
    if (lockedZoneCode) return lockedZoneCode;
    if (defaultZoneCode) return defaultZoneCode;
    return zones[0]?.code || '';
  }, [lockedZoneCode, defaultZoneCode, zones]);

  useEffect(() => {
    if (!open) return;
    if (editingGroup) {
      setPackageHr(normalizeRentalPackageHours(editingGroup.packageHr));
      setPackageKm(normalizeRentalPackageKm(editingGroup.packageKm));
      setZoneCode(editingGroup.zoneCode);
      const next: Record<string, VehiclePriceDraft> = {};
      const fallbackGrace = defaultGraceMinutes(serviceSlug, unifiedTransportRental);
      for (const v of rentalVehicles) {
        const match = editingGroup.vehiclePrices.find((p) => p.vehicleTypeCode === v.code);
        next[v.code] = {
          price: match && match.price > 0 ? match.price : null,
          graceTimeMin: match ? match.graceTimeMin : fallbackGrace,
          extraKmPrice: match ? match.extraKmPrice : 0,
        };
      }
      setPrices(next);
    } else {
      setPackageHr(defaultPackageHours(serviceSlug, unifiedTransportRental));
      setPackageKm(defaultPackageKm(serviceSlug, unifiedTransportRental));
      setZoneCode(initialZoneCode);
      setPrices(buildEmptyVehiclePrices(rentalVehicles, serviceSlug, unifiedTransportRental));
    }
  }, [open, editingGroup, rentalVehicles, serviceSlug, unifiedTransportRental, initialZoneCode]);

  useEffect(() => {
    if (!open) return;
    if (packageKm < RENTAL_PACKAGE_KM_MIN) setPackageKm(RENTAL_PACKAGE_KM_MIN);
    else if (packageKm > RENTAL_PACKAGE_KM_MAX) setPackageKm(RENTAL_PACKAGE_KM_MAX);
    if (packageHr < RENTAL_PACKAGE_HOURS_MIN) setPackageHr(RENTAL_PACKAGE_HOURS_MIN);
    else if (packageHr > RENTAL_PACKAGE_HOURS_MAX) setPackageHr(RENTAL_PACKAGE_HOURS_MAX);
  }, [open, packageKm, packageHr]);

  const setPackageHrSafe = (value: number) => {
    setPackageHr(normalizeRentalPackageHours(value, packageHr >= RENTAL_PACKAGE_HOURS_MIN ? packageHr : RENTAL_PACKAGE_HOURS_MIN));
  };

  const setPackageKmSafe = (value: number) => {
    setPackageKm(normalizeRentalPackageKm(value, packageKm >= RENTAL_PACKAGE_KM_MIN ? packageKm : RENTAL_PACKAGE_KM_MIN));
  };

  const patchPrice = (
    code: string,
    field: keyof VehiclePriceDraft,
    value: number | null,
  ) => {
    setPrices((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const vehicleRowErrors = useMemo(() => {
    const map = new Map<string, { price?: string; grace?: string; extraKm?: string }>();
    for (const v of rentalVehicles) {
      const row = prices[v.code];
      map.set(v.code, {
        price: validateRentalVehiclePrice(row?.price),
        grace: validateRentalGraceMinutes(row?.graceTimeMin),
        extraKm: validateRentalExtraKmPrice(row?.extraKmPrice),
      });
    }
    return map;
  }, [rentalVehicles, prices]);

  const vehiclePrices: RentalVehiclePrice[] = rentalVehicles.map((v) => ({
    vehicleTypeCode: v.code,
    vehicleTypeName: v.name,
    price: Number(prices[v.code]?.price || 0),
    graceTimeMin: Number(prices[v.code]?.graceTimeMin ?? defaultGraceMinutes(serviceSlug, unifiedTransportRental)),
    extraKmPrice: Number(prices[v.code]?.extraKmPrice || 0),
    rowId: editingGroup?.vehiclePrices.find((p) => p.vehicleTypeCode === v.code)?.rowId,
    rowCode: editingGroup?.vehiclePrices.find((p) => p.vehicleTypeCode === v.code)?.rowCode,
  }));

  const packageHrError = validateRentalPackageHours(packageHr);
  const packageKmError = validateRentalPackageKm(packageKm);
  const bundleError = validateRentalPackageBundle(packageHr, packageKm);

  const effectiveZoneCode = lockedZoneCode || zoneCode;
  const normalizedHr = normalizeRentalPackageHours(packageHr);
  const normalizedKm = normalizeRentalPackageKm(packageKm);

  const duplicateError = useMemo(() => getDuplicateRentalPackageMessage(existingGroups, {
    zoneCode: effectiveZoneCode,
    packageHr: normalizedHr,
    packageKm: normalizedKm,
    serviceSlug: serviceSlug || TRANSPORT_RENTAL_SERVICE_SLUG,
    excludeGroupCode: editingGroup?.packageGroupCode,
    ignoreServiceSlug: unifiedTransportRental,
  }), [existingGroups, effectiveZoneCode, normalizedHr, normalizedKm, serviceSlug, unifiedTransportRental, editingGroup?.packageGroupCode]);

  const allVehiclePricesValid = rentalVehicles.length > 0
    && rentalVehicles.every((v) => {
      const errors = vehicleRowErrors.get(v.code);
      return !errors?.price && !errors?.grace && !errors?.extraKm;
    });

  const canSave = !packageHrError
    && !packageKmError
    && !bundleError
    && !duplicateError
    && !!effectiveZoneCode
    && allVehiclePricesValid;

  const handleSave = () => {
    if (!canSave) return;
    const hr = normalizedHr;
    const km = normalizedKm;
    const form: RentalPackageFormState = {
      packageHr: hr,
      packageKm: km,
      zoneCode: effectiveZoneCode,
      packageGroupCode: editingGroup?.packageGroupCode || buildPackageGroupCode(effectiveZoneCode, hr, km),
      vehiclePrices,
    };
    onSave(form, editingGroup);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={800}>
            {editingGroup ? 'Edit Rental Package' : 'Add Rental Package'}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ opacity: 0.88, lineHeight: 1.5 }}>
            {unifiedTransportRental
              ? 'Hour/km bundle with per-vehicle pricing for all transport rental services.'
              : 'Hour/km bundle with per-vehicle pricing — same as transport rental management.'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <NonNegativeNumberField
                fullWidth
                size="small"
                label="Package Hours"
                value={packageHr}
                integer
                min={RENTAL_PACKAGE_HOURS_MIN}
                max={RENTAL_PACKAGE_HOURS_MAX}
                onValueChange={setPackageHrSafe}
                error={Boolean(packageHrError)}
                helperText={
                  packageHrError
                  || `${RENTAL_PACKAGE_HOURS_MIN}–${RENTAL_PACKAGE_HOURS_MAX.toLocaleString('en-IN')} hours (up to 1 month)`
                }
                FormHelperTextProps={helperTextProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <NonNegativeNumberField
                fullWidth
                size="small"
                label="Package KM"
                value={packageKm}
                integer
                min={RENTAL_PACKAGE_KM_MIN}
                max={RENTAL_PACKAGE_KM_MAX}
                onValueChange={setPackageKmSafe}
                error={Boolean(packageKmError)}
                helperText={
                  packageKmError
                  || `${RENTAL_PACKAGE_KM_MIN}–${RENTAL_PACKAGE_KM_MAX.toLocaleString('en-IN')} whole km`
                }
                FormHelperTextProps={helperTextProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {lockedZoneCode ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Zone"
                  value={
                    resolvedLockedZoneName
                      ? `${resolvedLockedZoneName} (${lockedZoneCode})`
                      : lockedZoneCode
                  }
                  disabled
                  sx={disabledFieldSx}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Zone"
                  value={zoneCode}
                  onChange={(e) => setZoneCode(e.target.value)}
                  disabled={!!editingGroup}
                >
                  {zones.map((z) => (
                    <MenuItem key={z._id} value={z.code || z._id}>
                      {z.name}{z.code ? ` (${z.code})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          </Grid>

          {bundleError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {bundleError}
            </Alert>
          )}

          {duplicateError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {duplicateError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
            <AlertTitle fontWeight={800}>Package details</AlertTitle>
            <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.25 }}>
              <Typography component="li" variant="body2">
                <strong>Package Hours & KM</strong> define the rental bundle shown during booking (e.g. 4h / 40 km).
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Zone</strong> links this package to the pickup area — only matching zones see these rates.
              </Typography>
              <Typography component="li" variant="body2">
                Enter <strong>price</strong> and <strong>grace period</strong> for every vehicle type below. Create Package stays disabled until all rows are valid.
              </Typography>
            </Stack>
          </Alert>

          <Paper variant="outlined" sx={{ overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'background.paper' }}>Vehicle</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'background.paper' }}>{chargeFieldLabel('Price', zoneCurrency)} *</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'background.paper' }}>Grace (min) *</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'background.paper' }}>{chargeFieldLabel('Extra KM', zoneCurrency)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rentalVehicles.map((v) => {
                  const row = prices[v.code];
                  const errors = vehicleRowErrors.get(v.code);
                  return (
                    <TableRow key={v.code}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color="text.primary">{v.name}</Typography>
                        <Typography variant="caption" color="text.primary" sx={{ opacity: 0.72 }}>{v.code}</Typography>
                      </TableCell>
                      <TableCell>
                        <NonNegativeNumberField
                          size="small"
                          value={row?.price ?? ''}
                          integer
                          min={RENTAL_PACKAGE_PRICE_MIN}
                          max={RENTAL_PACKAGE_PRICE_MAX}
                          maxDigits={RENTAL_PACKAGE_PRICE_MAX_DIGITS}
                          commitEmptyAsZero={false}
                          onEmpty={() => patchPrice(v.code, 'price', null)}
                          onValueChange={(value) => patchPrice(v.code, 'price', value)}
                          error={Boolean(errors?.price)}
                          helperText={errors?.price || `${moneyMark || ''}${RENTAL_PACKAGE_PRICE_MIN}–${RENTAL_PACKAGE_PRICE_MAX.toLocaleString('en-IN')} (max ${RENTAL_PACKAGE_PRICE_MAX_DIGITS} digits)`}
                          FormHelperTextProps={helperTextProps}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <NonNegativeNumberField
                          size="small"
                          value={row?.graceTimeMin ?? defaultGraceMinutes(serviceSlug, unifiedTransportRental)}
                          integer
                          min={RENTAL_PACKAGE_GRACE_MIN}
                          max={RENTAL_PACKAGE_GRACE_MAX}
                          maxDigits={RENTAL_PACKAGE_GRACE_MAX_DIGITS}
                          onValueChange={(value) => patchPrice(v.code, 'graceTimeMin', value)}
                          error={Boolean(errors?.grace)}
                          helperText={errors?.grace || `${RENTAL_PACKAGE_GRACE_MIN}–${RENTAL_PACKAGE_GRACE_MAX.toLocaleString('en-IN')} min (max ${RENTAL_PACKAGE_GRACE_MAX_DIGITS} digits)`}
                          FormHelperTextProps={helperTextProps}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <NonNegativeNumberField
                          size="small"
                          value={row?.extraKmPrice ?? 0}
                          integer
                          min={0}
                          max={RENTAL_PACKAGE_EXTRA_KM_MAX}
                          maxDigits={RENTAL_PACKAGE_EXTRA_KM_MAX_DIGITS}
                          onValueChange={(value) => patchPrice(v.code, 'extraKmPrice', value)}
                          error={Boolean(errors?.extraKm)}
                          helperText={errors?.extraKm || `0–${RENTAL_PACKAGE_EXTRA_KM_MAX.toLocaleString('en-IN')} (max ${RENTAL_PACKAGE_EXTRA_KM_MAX_DIGITS} digits)`}
                          FormHelperTextProps={helperTextProps}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!rentalVehicles.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.primary" sx={{ py: 2, textAlign: 'center', fontWeight: 600 }}>
                        No rental-enabled vehicle types. Add RENTAL to serviceTypes in Vehicle Type master.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!canSave || loading} onClick={handleSave}>
            {loading ? 'Saving…' : editingGroup ? 'Update Package' : 'Create Package'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

